import { useMemo } from 'react'

import type { GuestMedia } from '~publish/helpers/guestMedia'
import { getGuestMediaFileType } from '~publish/helpers/guestMedia'
import {
  GUEST_NETWORKS,
  getGuestNetworkService,
} from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import { Services } from '~publish/legacy/composer/composer/AppConstants'
import { getYoutubeTitleFromText } from '~publish/legacy/composer/composer/components/youtube/utils'
import { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import type { Image } from '~publish/legacy/composer/composer/entities/factories'
import type { ValidationFeedback } from '~publish/legacy/composer/composer/stores/types'
import { getDraftsFeedback } from '~publish/legacy/composer/composer/stores/utils'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

export type GuestNetworkValidation = Readonly<{
  drafts: ReadonlyMap<GuestNetworkId, Draft>
  whatPreventsSaving: readonly ValidationFeedback[]
}>

function getGuestValidationImages(media: readonly GuestMedia[]): Image[] {
  return media.map((asset) => ({
    url: asset.assetUrl,
    name: asset.fileName,
    size: asset.size,
    width: asset.width ?? null,
    height: asset.height ?? null,
    altText: asset.altText ?? null,
    mediaType: getGuestMediaFileType(asset.mimeType),
  }))
}

export function useGuestNetworkValidation({
  networks,
  editor,
  text,
  media,
}: {
  networks: readonly GuestNetworkId[]
  editor: BufferEditor
  text: string
  media: readonly GuestMedia[]
}): GuestNetworkValidation {
  return useMemo(() => {
    const images = getGuestValidationImages(media)
    const drafts = new Map<GuestNetworkId, Draft>()
    const draftsByService = new Map<string, Draft>()

    for (const network of GUEST_NETWORKS) {
      if (!networks.includes(network.id)) continue
      const serviceName = getGuestNetworkService(network)
      const existingDraft = draftsByService.get(serviceName)
      if (existingDraft) {
        drafts.set(network.id, existingDraft)
        continue
      }
      const service = Services.get(serviceName)
      if (!service) continue

      const draft = new Draft(service, editor)
      draft.text = text
      draft.isEnabled = true
      draft.setImages(images)
      const youtubeData = draft.channelData?.youtube
      if (draft.service.isYoutube() && youtubeData) {
        draft.setChannelData({
          ...youtubeData,
          title: getYoutubeTitleFromText(text),
        })
      }
      drafts.set(network.id, draft)
      draftsByService.set(serviceName, draft)
    }

    return {
      drafts,
      whatPreventsSaving: getDraftsFeedback({
        drafts: [...draftsByService.values()],
        isDraft: false,
      }),
    }
  }, [networks, editor, text, media])
}
